// ─────────────────────────────────────────────────────────────────────────────
// Automation Insights — visit tracking / leaderboard service (added 2026-08-25)
//
// Deliberately isolated from automationInsightsService.ts. pingVisit() and
// sendActiveBeat() are fire-and-forget and swallow every error internally by
// design — a failed or slow call must never surface anywhere in the UI or
// affect any other part of the page (matches the backend's /visits handlers,
// which also never fail their own request on a DB problem). fetchLeaderboard()
// is a normal read call; its caller (VisitLeaderboard.tsx) owns its own
// independent loading/error state.
//
// 2026-09-11: added sendActiveBeat() (real active-time tracking, replacing
// the old wall-clock duration) and a date range on fetchLeaderboard().
// ─────────────────────────────────────────────────────────────────────────────

import { AUTOMATION_INSIGHTS_ENDPOINTS } from '../config/api';
import { authFetch } from './authFetch';

export interface LeaderboardEntry {
  user_oid: string;
  user_name: string;
  user_email: string | null;
  visit_count: number;
  avg_duration_seconds: number;
  last_visit_at: string | null;
}

export interface LeaderboardResult {
  leaders: LeaderboardEntry[];
  ranged: boolean;
}

/**
 * Record or refresh one visit session's identity. Never throws — any
 * failure (network, auth, backend) is caught and only logged to console.
 * Safe to call repeatedly with the same sessionId (upserted server-side).
 */
export async function pingVisit(sessionId: string): Promise<void> {
  try {
    const response = await authFetch(AUTOMATION_INSIGHTS_ENDPOINTS.VISITS.PING, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    });
    if (!response.ok) {
      console.warn(`visit ping failed (non-fatal): ${response.status} ${response.statusText}`);
    }
  } catch (err) {
    // Deliberately silent beyond a console warning — see module docstring.
    console.warn('visit ping failed (non-fatal):', err);
  }
}

/**
 * Report a slice of real active time (tab visible + window focused) for a
 * session. Never throws — same fire-and-forget contract as pingVisit().
 *
 * `keepalive` should be set to true only when called from a
 * visibilitychange/pagehide handler as the page is being hidden/unloaded —
 * it tells the browser to try to complete the request even after the page
 * is gone (the fetch-level replacement for navigator.sendBeacon; sendBeacon
 * itself can't be used here because it doesn't support the Authorization
 * header authFetch needs to attach).
 */
export async function sendActiveBeat(
  sessionId: string,
  activeSeconds: number,
  opts: { keepalive?: boolean } = {}
): Promise<void> {
  if (activeSeconds <= 0) return;
  try {
    const response = await authFetch(AUTOMATION_INSIGHTS_ENDPOINTS.VISITS.BEAT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId, active_seconds: activeSeconds }),
      keepalive: opts.keepalive ?? false,
    });
    if (!response.ok) {
      console.warn(`visit beat failed (non-fatal): ${response.status} ${response.statusText}`);
    }
  } catch (err) {
    console.warn('visit beat failed (non-fatal):', err);
  }
}

/**
 * Throws on failure — the leaderboard card catches this and shows nothing.
 * Pass dateFrom/dateTo (YYYY-MM-DD) to get a range-filtered leaderboard;
 * omit both for the existing all-time behavior.
 */
export async function fetchLeaderboard(dateFrom?: string, dateTo?: string): Promise<LeaderboardResult> {
  const params = new URLSearchParams();
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);
  const query = params.toString();
  const url = query ? `${AUTOMATION_INSIGHTS_ENDPOINTS.VISITS.LEADERBOARD}?${query}` : AUTOMATION_INSIGHTS_ENDPOINTS.VISITS.LEADERBOARD;

  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Leaderboard API error (${response.status})`);
  }
  const data = await response.json();
  return {
    leaders: Array.isArray(data?.leaders) ? data.leaders : [],
    ranged: Boolean(data?.ranged),
  };
}
